import React, { useState } from 'react';
import { X, User, Mail, Calendar, LogOut, Trash2, Shield, AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '../services/supabase';

export default function UserProfileModal({ isOpen, onClose, user, onLogout, t, isMobile }) {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!isOpen || !user) return null;

    const registrationDate = user.created ? new Date(user.created).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    }) : 'N/A';

    const handleDeleteAccount = async () => {
        setLoading(true);
        try {
            // RPC com security definer: apaga auth.users -> cascade remove profile + dados
            const { error } = await supabase.rpc('delete_own_account');
            if (error) throw error;
            await supabase.auth.signOut();
            if (onLogout) onLogout();
            onClose();
        } catch (error) {
            console.error('Error deleting account:', error.message || error);
            alert('Erro ao excluir conta. Verifique sua conexão ou tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
            padding: 20
        }}>
            <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} />

            <div style={{
                background: t.bgCard,
                borderRadius: 2,
                border: `1.5px solid ${t.ink}`,
                width: isMobile ? '92%' : '100%',
                maxWidth: 400,
                position: 'relative',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
                overflow: 'hidden',
                animation: 'modalSlideUp 0.3s ease-out'
            }}>
                {/* Header */}
                <div style={{ padding: '24px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                            <User size={20} />
                        </div>
                        <div>
                            <h2 style={{ color: t.text, margin: 0, fontSize: 18, fontWeight: 700 }}>Meu Perfil</h2>
                            <p style={{ margin: 0, fontSize: 12, color: t.textMuted }}>Configurações de conta</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer', padding: 4 }}>
                        <X size={20} />
                    </button>
                </div>

                <div style={{ padding: isMobile ? 20 : 24 }}>
                    {/* Info items */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Mail size={16} color={t.textMuted} />
                            <div>
                                <span style={{ display: 'block', fontSize: 11, color: t.textMuted, textTransform: 'uppercase' }}>E-mail</span>
                                <span style={{ fontSize: 14, color: t.text, fontWeight: 500 }}>{user.email}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Calendar size={16} color={t.textMuted} />
                            <div>
                                <span style={{ display: 'block', fontSize: 11, color: t.textMuted, textTransform: 'uppercase' }}>Membro desde</span>
                                <span style={{ fontSize: 14, color: t.text, fontWeight: 500 }}>{registrationDate}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Shield size={16} color={t.textMuted} />
                            <div>
                                <span style={{ display: 'block', fontSize: 11, color: t.textMuted, textTransform: 'uppercase' }}>Nível de Acesso</span>
                                <span style={{ fontSize: 14, color: user.role === 'admin' ? t.accent : t.text, fontWeight: 600 }}>
                                    {user.role === 'admin' ? 'Administrador' : 'Operador'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {!showDeleteConfirm ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <button
                                onClick={() => { onLogout(); onClose(); }}
                                style={{
                                    width: '100%', padding: '12px', borderRadius: 8,
                                    border: `1px solid ${t.border}`, background: t.bgInput,
                                    color: t.text, fontSize: 14, fontWeight: 600,
                                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: 8, transition: 'all 0.2s'
                                }}
                            >
                                <LogOut size={16} /> Sair do Sistema
                            </button>

                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                style={{
                                    width: '100%', padding: '12px', borderRadius: 8,
                                    border: 'none', background: 'transparent',
                                    color: t.danger, fontSize: 13, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: 8, opacity: 0.7
                                }}
                            >
                                <Trash2 size={14} /> Excluir minha conta
                            </button>
                        </div>
                    ) : (
                        <div style={{
                            background: 'rgba(239, 68, 68, 0.05)',
                            border: `1px solid ${t.danger}30`,
                            borderRadius: 12, padding: 16,
                            animation: 'fadeIn 0.2s ease-out'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: t.danger, marginBottom: 8 }}>
                                <AlertTriangle size={18} />
                                <span style={{ fontSize: 14, fontWeight: 700 }}>Tem certeza?</span>
                            </div>
                            <p style={{ fontSize: 12, color: t.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
                                Esta ação é irreversível. Todos os seus dados serão removidos permanentemente.
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    style={{
                                        flex: 1, padding: '8px', borderRadius: 6,
                                        border: `1px solid ${t.border}`, background: t.bgCard,
                                        color: t.textSecondary, fontSize: 13, cursor: 'pointer'
                                    }}
                                    disabled={loading}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleDeleteAccount}
                                    style={{
                                        flex: 1, padding: '8px', borderRadius: 6,
                                        border: 'none', background: t.danger,
                                        color: '#fff', fontSize: 13, fontWeight: 600,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: 6
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? <Loader2 size={14} className="animate-spin" /> : 'Confirmar Exclusão'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ background: t.bgInput, padding: '12px 24px', textAlign: 'center', borderTop: `1px solid ${t.border}` }}>
                    <p style={{ margin: 0, fontSize: 10, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '1px' }}>
                        ID: {user.id}
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes modalSlideUp {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
}
